import { useContext, useEffect, useRef } from 'react';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { MapContext } from '../contexts/MapContext';
import { sectionTypes } from '../constants/fields';
import { mapModes } from '../constants/map';
import {
  handleBackspaceWhileEditing,
  handleEscapeWhileDrawing,
} from '../utils/draw';

const DRAW_MODES = [mapModes.DRAW, mapModes.EDIT];
const mapboxDrawModes = {
  POLYGON: 'draw_polygon',
  LINE_STRING: 'draw_line_string',
};

export default (map) => {
  const {
    fields: { updateFieldSection },
    mapState: {
      activeField,
      setMapMode,
      mapMode,
      context: { keylineMode = false },
      drawRef,
    },
  } = useContext(MapContext);

  const updateFieldSectionRef = useRef();
  updateFieldSectionRef.current = updateFieldSection;

  useEffect(() => {
    if (!DRAW_MODES.includes(mapMode)) return undefined;
    const { type, id, rowFeature } = activeField;
    const featureKey = keylineMode ? 'rowFeature' : 'fieldFeature';
    const feature = activeField[featureKey];
    const drawMode =
      type === sectionTypes.LINE || keylineMode
        ? mapboxDrawModes.LINE_STRING
        : mapboxDrawModes.POLYGON;
    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      defaultMode: drawMode,
    });
    const handleDone = () => {
      if (type === sectionTypes.KEYLINE && !keylineMode && !rowFeature) {
        setMapMode(mapModes.DRAW, id, { keylineMode: true });
      } else {
        setMapMode(mapModes.SECTION_ENGAGED);
      }
    };
    const updateResult = () => {
      const newFeature = drawRef.current.getAll().features[0];
      updateFieldSectionRef.current(id, {
        [featureKey]: newFeature,
      });
      if (mapMode === mapModes.DRAW) {
        // Defer setMapMode to avoid interfering with MapboxDraw's internal event handling
        setTimeout(() => handleDone(), 0);
      }
    };
    const handleKeyDown = (e) => {
      if (mapMode === mapModes.EDIT) {
        handleBackspaceWhileEditing(e, drawRef.current);
      }
      if (mapMode === mapModes.DRAW || mapMode === mapModes.EDIT) {
        handleEscapeWhileDrawing(e, setMapMode, mapModes.SECTION_ENGAGED);
      }
    };

    map.addControl(drawRef.current);
    if (mapMode === mapModes.EDIT) {
      // Focus the map canvas so that keyboard events register
      map.getCanvas().focus();
      drawRef.current.add({ ...feature, id });
      drawRef.current.changeMode('direct_select', {
        featureId: id,
      });
    }
    map.on('draw.create', updateResult);
    map.on('draw.update', updateResult);
    map.on('draw.modechange', handleDone);
    map.getCanvas().addEventListener('keydown', handleKeyDown);

    return () => {
      map.removeControl(drawRef.current);
      map.off('draw.create', updateResult);
      map.off('draw.update', updateResult);
      map.off('draw.modechange', handleDone);
      map.getCanvas().removeEventListener('keydown', handleKeyDown);
      drawRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapMode, keylineMode]);
};
